import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import {
  fetchGSTBalances,
  recordGSTPayment,
  fetchGSTPaymentHistory,
  fetchGSTPaymentById,
  fetchGSTInvoiceBreakdown
} from "./gstSettlementThunk";
import { IGSTSettlementSliceState } from "../../utils/gstSettlement.types";

/**
 * State slice initial state.
 */
const initialState: IGSTSettlementSliceState = {
  balances: null,
  payments: [],
  currentPayment: null,
  invoiceBreakdown: null,
  expandedPartyInvoices: {},
  status: ApiStatus.IDLE,
  balancesStatus: ApiStatus.IDLE,
  paymentStatus: ApiStatus.IDLE,
  historyStatus: ApiStatus.IDLE,
  invoiceBreakdownStatus: ApiStatus.IDLE,
  errorMessage: ''
};

/**
 * GST Settlement State Slice.
 */
const gstSettlementSlice = createSlice({
  name: 'gstSettlement',
  initialState: initialState,
  reducers: {
    resetPaymentStatus: (state) => {
      state.paymentStatus = ApiStatus.IDLE;
      state.errorMessage = '';
    },
    clearBalances: (state) => {
      state.balances = null;
      state.balancesStatus = ApiStatus.IDLE;
    },
    clearInvoiceBreakdown: (state) => {
      state.invoiceBreakdown = null;
      state.expandedPartyInvoices = {};
      state.invoiceBreakdownStatus = ApiStatus.IDLE;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch GST Balances
      .addCase(fetchGSTBalances.pending, (state, action) => {
        state.balancesStatus = ApiStatus.LOADING;
      })
      .addCase(fetchGSTBalances.fulfilled, (state, action) => {
        state.balancesStatus = ApiStatus.SUCCESS;
        state.balances = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchGSTBalances.rejected, (state, action) => {
        state.balancesStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch GST balances');
      })

      // Record GST Payment
      .addCase(recordGSTPayment.pending, (state, action) => {
        state.paymentStatus = ApiStatus.LOADING;
      })
      .addCase(recordGSTPayment.fulfilled, (state, action) => {
        state.paymentStatus = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.payments = [action.payload, ...state.payments];
        state.errorMessage = '';
        toast.success('GST payment recorded successfully');
      })
      .addCase(recordGSTPayment.rejected, (state, action) => {
        state.paymentStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error(action.error.message || 'Failed to record GST payment');
      })

      // Fetch GST Payment History
      .addCase(fetchGSTPaymentHistory.pending, (state, action) => {
        state.historyStatus = ApiStatus.LOADING;
      })
      .addCase(fetchGSTPaymentHistory.fulfilled, (state, action) => {
        state.historyStatus = ApiStatus.SUCCESS;
        state.payments = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchGSTPaymentHistory.rejected, (state, action) => {
        state.historyStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch payment history');
      })

      // Fetch GST Payment By ID
      .addCase(fetchGSTPaymentById.pending, (state, action) => {
        state.status = ApiStatus.LOADING;
      })
      .addCase(fetchGSTPaymentById.fulfilled, (state, action) => {
        state.status = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchGSTPaymentById.rejected, (state, action) => {
        state.status = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch payment details');
      })

      // Fetch GST Invoice Breakdown
      .addCase(fetchGSTInvoiceBreakdown.pending, (state) => {
        state.invoiceBreakdownStatus = ApiStatus.LOADING;
      })
      .addCase(fetchGSTInvoiceBreakdown.fulfilled, (state, action) => {
        state.invoiceBreakdownStatus = ApiStatus.SUCCESS;
        // If it's a party drill-down, store in expandedPartyInvoices
        if (action.payload.partyInvoices) {
          const { partyId, invoices } = action.payload.partyInvoices;
          state.expandedPartyInvoices[partyId] = invoices;
        } else {
          state.invoiceBreakdown = action.payload;
        }
      })
      .addCase(fetchGSTInvoiceBreakdown.rejected, (state, action) => {
        state.invoiceBreakdownStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch invoice breakdown');
      });
  }
});

export const { resetPaymentStatus, clearBalances, clearInvoiceBreakdown } = gstSettlementSlice.actions;
export default gstSettlementSlice.reducer;
